// src/admin-pages/AdminBookings.jsx
import React, { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import Topbar  from "./Topbar";
import { adminGetBookingsApi, adminConfirmBookingApi, adminUpdateStatusApi } from "../api/bookingApi";
import "./admin-bookings.css";

const STATUS_COLORS = {
  pending_payment:       { bg:"#FEF3C7", color:"#92400E", label:"Pending Payment" },
  payment_failed:        { bg:"#FEE2E2", color:"#991B1B", label:"Payment Failed"  },
  awaiting_confirmation: { bg:"#DBEAFE", color:"#1E40AF", label:"Awaiting Confirmation" },
  confirmed:             { bg:"#D1FAE5", color:"#065F46", label:"Confirmed"        },
  in_progress:           { bg:"#E0E7FF", color:"#3730A3", label:"In Progress"      },
  completed:             { bg:"#D1FAE5", color:"#14532D", label:"Completed"        },
  cancelled:             { bg:"#F3F4F6", color:"#6B7280", label:"Cancelled"        },
};

const TIME_LABELS = {
  early:"Before 08 AM", morning:"08 AM – 12 PM",
  afternoon:"12 PM – 04 PM", evening:"After 04 PM"
};

export default function AdminBookings() {
  const [bookings,  setBookings]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState({ status:"", search:"", date:"" });
  const [selected,  setSelected]  = useState(null);  // booking for action modal
  const [action,    setAction]    = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [saving,    setSaving]    = useState(false);

  const loadBookings = async () => {
    setLoading(true);
    try {
      // Remove empty string values so backend doesn't filter on them
      const cleanFilter = Object.fromEntries(
        Object.entries(filter).filter(([_, v]) => v !== "")
      );
      const res = await adminGetBookingsApi(cleanFilter);
      setBookings(res.data.bookings);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadBookings(); }, [filter.status, filter.date]); // eslint-disable-line

  const handleSearch = (e) => {
    if(e.key==="Enter") loadBookings();
  };

  const openModal = (bk, act) => {
    setSelected(bk);
    setAction(act);
    setAdminNote(bk.admin_note||"");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if(action==="confirm") {
        await adminConfirmBookingApi(selected.id, { admin_note: adminNote });
      } else {
        await adminUpdateStatusApi(selected.id, { status: action, admin_note: adminNote });
      }
      setSelected(null);
      loadBookings();
    } catch(e) { alert("Action failed"); }
    finally { setSaving(false); }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}) : "—";
  const fmtMoney = (n) => n ? `₹${parseFloat(n).toLocaleString("en-IN")}` : "—";

  return (
    <>
      <div className="adm-layout">
        <Sidebar active="bookings"/>
        <main className="adm-main">
          <div className="adm-shell">
            <Topbar title="Service Bookings" subtitle="Manage & confirm all bookings"/>

            {/* ── FILTERS ── */}
            <div className="abk-filters">
              <input className="abk-search" placeholder="Search user name, phone, service..."
                value={filter.search}
                onChange={e=>setFilter(p=>({...p,search:e.target.value}))}
                onKeyDown={handleSearch}/>

              <select className="abk-select" value={filter.status}
                onChange={e=>setFilter(p=>({...p,status:e.target.value}))}>
                <option value="">All Statuses</option>
                {Object.entries(STATUS_COLORS).map(([k,v])=>(
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>

              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <input type="date" className="abk-select" value={filter.date}
                  onChange={e=>setFilter(p=>({...p,date:e.target.value}))}/>
                {filter.date && (
                  <button className="abk-refresh" style={{padding:"10px 12px"}}
                    onClick={()=>setFilter(p=>({...p,date:""}))}>✕ Clear</button>
                )}
              </div>

              <button className="abk-refresh" onClick={loadBookings}>🔄 Refresh</button>
            </div>

            {/* ── PENDING BADGE ── */}
            {bookings.filter(b=>b.status==="awaiting_confirmation").length > 0 && (
              <div className="abk-alert">
                ⏳ <strong>{bookings.filter(b=>b.status==="awaiting_confirmation").length}</strong> booking(s) waiting for your confirmation!
              </div>
            )}

            {/* ── TABLE ── */}
            {loading ? (
              <div className="abk-loading">Loading bookings...</div>
            ) : bookings.length===0 ? (
              <div className="abk-empty">No bookings found.</div>
            ) : (
              <div className="abk-table-wrap">
                <table className="abk-table">
                  <thead>
                    <tr>
                      <th>ID</th><th>User</th><th>Service</th>
                      <th>Date & Time</th><th>Amount</th>
                      <th>Payment</th><th>Status</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map(bk=>{
                      const sc = STATUS_COLORS[bk.status]||STATUS_COLORS.cancelled;
                      return (
                        <tr key={bk.id}>
                          <td className="abk-id">#BK{String(bk.id).padStart(5,"0")}</td>
                          <td>
                            <div className="abk-user-name">{bk.user_name}</div>
                            <div className="abk-user-ph">{bk.user_phone}</div>
                          </td>
                          <td>
                            <div className="abk-svc-name">{bk.service_name}</div>
                            <div className="abk-svc-cat">{bk.category}</div>
                          </td>
                          <td>
                            <div>{fmt(bk.booking_date)}</div>
                            <div className="abk-slot">{TIME_LABELS[bk.time_slot]}</div>
                          </td>
                          <td>{fmtMoney(bk.price)}</td>
                          <td>
                            <span className="abk-pay-badge" style={{
                              background: bk.payment_status==="paid"?"#D1FAE5":"#FEF3C7",
                              color: bk.payment_status==="paid"?"#065F46":"#92400E"
                            }}>
                              {bk.payment_status==="paid"?"✓ Paid":bk.payment_status||"—"}
                            </span>
                          </td>
                          <td>
                            <span className="abk-status" style={{background:sc.bg,color:sc.color}}>
                              {sc.label}
                            </span>
                          </td>
                          <td>
                            <div className="abk-actions">
                              {bk.status==="awaiting_confirmation" && (
                                <button className="abk-btn abk-btn-confirm"
                                  onClick={()=>openModal(bk,"confirm")}>
                                  ✓ Confirm
                                </button>
                              )}
                              {bk.status==="confirmed" && (
                                <button className="abk-btn abk-btn-progress"
                                  onClick={()=>openModal(bk,"in_progress")}>
                                  Start
                                </button>
                              )}
                              {bk.status==="in_progress" && (
                                <button className="abk-btn abk-btn-done"
                                  onClick={()=>openModal(bk,"completed")}>
                                  Done
                                </button>
                              )}
                              {!["completed","cancelled","payment_failed"].includes(bk.status) && (
                                <button className="abk-btn abk-btn-cancel"
                                  onClick={()=>openModal(bk,"cancelled")}>
                                  Cancel
                                </button>
                              )}
                              <button className="abk-btn abk-btn-view"
                                onClick={()=>openModal(bk,"view")}>
                                View
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* ── ACTION MODAL ── */}
      {selected && (
        <div className="abk-modal-overlay" onClick={()=>setSelected(null)}>
          <div className="abk-modal" onClick={e=>e.stopPropagation()}>
            <div className="abk-modal-head">
              <h3>
                {action==="view"      ? "Booking Details"
                :action==="confirm"   ? "✓ Confirm Booking"
                :action==="cancelled" ? "Cancel Booking"
                :action==="completed" ? "Mark as Completed"
                :action==="in_progress"?"Mark as In Progress"
                :"Update Booking"}
              </h3>
              <button className="abk-modal-close" onClick={()=>setSelected(null)}>✕</button>
            </div>

            <div className="abk-modal-body">
              <div className="abk-detail-grid">
                <div><label>Booking ID</label><span>#BK{String(selected.id).padStart(5,"0")}</span></div>
                <div><label>User</label><span>{selected.user_name} ({selected.user_phone})</span></div>
                <div><label>Service</label><span>{selected.service_name}</span></div>
                <div><label>Category</label><span>{selected.category}</span></div>
                <div><label>Date</label><span>{fmt(selected.booking_date)}</span></div>
                <div><label>Time Slot</label><span>{TIME_LABELS[selected.time_slot]}</span></div>
                <div><label>Address</label><span>{selected.line1}, {selected.city}</span></div>
                <div><label>Amount</label><span>{fmtMoney(selected.price)}</span></div>
                <div><label>Payment</label><span>{selected.payment_status}</span></div>
                {selected.instructions&&(
                  <div className="abk-full"><label>Instructions</label><span>{selected.instructions}</span></div>
                )}
              </div>

              {action!=="view" && (
                <>
                  <div className="abk-modal-note-label">Admin Note (optional)</div>
                  <textarea className="abk-modal-textarea" rows={3}
                    placeholder="Add internal note..."
                    value={adminNote} onChange={e=>setAdminNote(e.target.value)}/>
                </>
              )}
            </div>

            <div className="abk-modal-foot">
              <button className="abk-modal-cancel" onClick={()=>setSelected(null)}>Close</button>
              {action!=="view"&&(
                <button className="abk-modal-save"
                  onClick={handleSave} disabled={saving}>
                  {saving?"Saving...":
                   action==="confirm"?"Confirm Booking":
                   action==="cancelled"?"Cancel Booking":
                   "Update Status"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}